import { CartIcon } from "@/components/icons/cart-icon";
import { HbLogo } from "@/components/icons/hb-logo";
import { UserIcon } from "@/components/icons/user-icon";
import DesktopNavigationEntries from "@/components/layout/header/desktop-navigation-entries";
import LocaleSwitcher from "@/components/layout/locale-switcher";
import getShopDomain from "@/util/get-shop-domain";
import { Locale } from "@/i18n/i18n.config";
import { useTranslations } from "@/libs/base/components/locale-provider";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import React, { createContext, useContext } from "react";
import { RawHeaderNavigationEntry } from "../header";

interface PageProps {
  navigationEntries: RawHeaderNavigationEntry[];
  locale: Locale;
  selectableLocales: Locale[];
  selectLocale: (locale: Locale) => void;
  isBigHeaderShown: boolean;
  region: string;
}

const HeaderContext = createContext<{ isBig: boolean }>(null!);
export function useHeader() {
  const ctx = useContext(HeaderContext);
  if (!ctx) throw new Error("useHeader() needs to be used inside a <HeaderContext/>");

  return ctx;
}

export default function DesktopHeader({
  navigationEntries,
  locale,
  selectableLocales,
  selectLocale,
  isBigHeaderShown,
  region,
}: PageProps) {
  const bigHeader = {
    width: 83,
    height: 114,
  };
  const smallHeader = {
    width: 29,
    height: 40,
  };

  const shopDomain = getShopDomain(region, locale);
  const { navbar: translations } = useTranslations();

  return (
    <header className="fixed top-0 z-20 hidden w-full bg-lightgray-200 lg:block">
      <nav aria-label="Top">
        <div className="border-b border-lightgray-300">
          <div
            className={cn(
              "flex items-center",
              "transition-[height] duration-[256ms] ease-[ease]",
              isBigHeaderShown ? "h-[155px]" : "h-[50px]",
            )}
          >
            <HeaderContext.Provider value={{ isBig: isBigHeaderShown }}>
              {/* Flyout menus */}
              <DesktopNavigationEntries navigationEntries={navigationEntries} />
            </HeaderContext.Provider>

            {/* Logo */}
            <div className="absolute inset-0 z-0 flex flex-1 items-center justify-center px-2">
              <TranslatedLink href="/" className="flex justify-center">
                <span className="sr-only">Hering Berlin</span>
                <HbLogo
                  width={isBigHeaderShown ? bigHeader.width : smallHeader.width}
                  height={isBigHeaderShown ? bigHeader.height : smallHeader.height}
                  showBrandName={isBigHeaderShown}
                  className={cn("transition-all duration-[256ms] ease-[ease]", !isBigHeaderShown && "mt-2")}
                />
              </TranslatedLink>
            </div>

            {/* Icons */}
            <div className="z-30 mx-8 ml-auto flex items-center gap-5">
              <a href={shopDomain + "/shop/"} aria-label={translations.cart}>
                <CartIcon />
              </a>
              <a href={shopDomain + "/customer/account/login/"} aria-label={translations.account}>
                <UserIcon />
              </a>
              <LocaleSwitcher locale={locale} selectableLocales={selectableLocales} selectLocale={selectLocale} />
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
